import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiFlag, FiX, FiCheck, FiAlertTriangle } = FiIcons

const ReportBrokenLinkModal = ({ isOpen, opportunity, onClose, onSubmit }) => {
  const [isConfirmed, setIsConfirmed] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  const handleSubmit = async () => {
    if (!isConfirmed) return
    
    setIsSubmitting(true)
    
    try {
      // Send email via mailto link
      const subject = encodeURIComponent(`Broken Link: ${opportunity?.link_opportunity || 'Unknown'}`)
      const body = encodeURIComponent(`
Broken Link Report:
-------------------
Link: ${opportunity?.link_opportunity || 'Unknown'}
Type: ${opportunity?.type || 'Unknown'}
Niche: ${opportunity?.niche || 'Unknown'}
Contact: ${opportunity?.contact || 'Unknown'}
-------------------
This link has been reported as broken by a user.
      `)
      
      window.open(`mailto:k9links@leadsurance.com?subject=${subject}&body=${body}`, '_blank')
      
      // Show success message and submit to backend
      setIsSuccess(true)
      await onSubmit(true)
      
      // Close modal after 2 seconds
      setTimeout(() => {
        setIsConfirmed(false)
        setIsSuccess(false)
        onClose()
      }, 2000)
    } catch (error) {
      console.error('Error reporting broken link:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="bg-white rounded-2xl shadow-xl max-w-md w-full"
        >
          <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="bg-red-100 p-2 rounded-full mr-3">
                  <SafeIcon icon={FiFlag} className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">Report Broken Link</h3>
              </div>
              <button
                onClick={onClose}
                className="p-1 rounded-full hover:bg-gray-100 transition-colors"
              >
                <SafeIcon icon={FiX} className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {isSuccess ? (
              <div className="py-6 text-center">
                <div className="bg-green-100 p-3 rounded-full mx-auto mb-4 w-16 h-16 flex items-center justify-center">
                  <SafeIcon icon={FiCheck} className="w-8 h-8 text-green-600" />
                </div>
                <h4 className="text-xl font-semibold text-gray-900 mb-2">Thank You!</h4>
                <p className="text-gray-600">
                  You've successfully reported this broken link to our support team.
                </p>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <p className="text-gray-600 mb-4">
                    Please confirm that the following link is broken or no longer functioning:
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg mb-4">
                    <p className="font-medium text-gray-900 break-words">{opportunity?.link_opportunity}</p>
                    <p className="text-sm text-gray-600 mt-1">{opportunity?.type} • {opportunity?.niche}</p>
                  </div>
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 flex items-start">
                    <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-yellow-500 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-yellow-700">
                      Reporting will send an email to our support team. Please only report links that are actually broken.
                    </p>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={isConfirmed}
                      onChange={(e) => setIsConfirmed(e.target.checked)}
                      className="w-4 h-4 text-[#D59A4C] border-gray-300 rounded focus:ring-[#D59A4C]"
                    />
                    <span className="ml-2 text-gray-700">
                      I confirm this link no longer works
                    </span>
                  </label>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={!isConfirmed || isSubmitting}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white mr-2"></div>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <SafeIcon icon={FiFlag} className="w-4 h-4 mr-2" />
                        Confirm & Send
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  )
}

export default ReportBrokenLinkModal